"use client";

import { useState, useEffect, useMemo } from "react";
import type { ChallengeZoneGame } from "./types";
import type { ChallengeZoneResponse } from "./types";
import { sortAndEnrichGames } from "./sortGames";
interface UseChallengeZoneGamesResult {
  games: ChallengeZoneGame[];
  isLoading: boolean;
}

/**
 * Fetches and sorts Challenge Zone games for the given related entities.
 * Extracts team entity IDs, fetches matching games, returns sorted + enriched results.
 * Returns empty array if no team entities or no games found.
 */
export function useChallengeZoneGames(relatedEntities: Array<{
  entityId: string;
  type: string;
}>, locale: string): UseChallengeZoneGamesResult {
  const [games, setGames] = useState<ChallengeZoneGame[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Extract team entity IDs — stable reference via useMemo
  const teamEntityIds = useMemo(() => relatedEntities.filter(e => e.type === "team").map(e_0 => e_0.entityId), [relatedEntities]);
  useEffect(() => {
    if (teamEntityIds.length === 0) {
      setGames([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    async function fetchGames() {
      try {
        const params = new URLSearchParams({
          entityIds: teamEntityIds.join(","),
          language: locale
        });
        const res = await fetch(`/api/challenge-zone?${params}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: ChallengeZoneResponse = await res.json();
        if (!cancelled) {
          setGames(sortAndEnrichGames(data.games));
        }
      } catch (err) {
        console.error("[ChallengeZone] Failed to fetch games:", err);
        if (!cancelled) {
          setGames([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }
    fetchGames();
    return () => {
      cancelled = true;
    };
  }, [teamEntityIds, locale]);
  return {
    games,
    isLoading
  };
}